import React, { useState, useEffect } from 'react';
import { getRooms, getTables, createRoom, updateRoom, deleteRoom } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { UtensilsCrossed, Plus, Pencil, Trash2, Loader2, RefreshCw, Table2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRooms() {
  const [rooms, setRooms] = useState([]);
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sort_order: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [r, t] = await Promise.all([getRooms(), getTables()]);
      setRooms(r);
      setTables(t);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (room = null) => {
    setEditing(room);
    setForm(room ? { name: room.name, sort_order: room.sort_order ?? 0 } : { name: '', sort_order: rooms.length });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bitte Namen eingeben'); return; }
    try {
      if (editing) {
        await updateRoom(editing.id, form.name, form.sort_order);
        toast.success('Raum aktualisiert');
      } else {
        await createRoom(form.name, form.sort_order);
        toast.success('Raum erstellt');
      }
      setShowDialog(false);
      loadData();
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (room) => {
    const roomTableCount = tables.filter(t => t.room_id === room.id).length;
    if (roomTableCount > 0) {
      toast.error(`Raum hat noch ${roomTableCount} Tische. Bitte zuerst Tische entfernen.`);
      return;
    }
    if (!window.confirm(`Raum "${room.name}" wirklich löschen?`)) return;
    try {
      await deleteRoom(room.id);
      toast.success('Raum gelöscht');
      loadData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Räume</h1>
          <p className="text-slate-500">{rooms.length} Räume · {tables.length} Tische gesamt</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Raum
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Raum</TableHead>
                <TableHead className="text-center">Tische</TableHead>
                <TableHead className="text-center">Sortierung</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.map((room) => {
                const tableCount = tables.filter(t => t.room_id === room.id).length;
                return (
                  <TableRow key={room.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                          <UtensilsCrossed className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="font-medium">{room.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5 text-slate-600">
                        <Table2 className="w-3.5 h-3.5" />
                        <span>{tableCount}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-slate-500">{room.sort_order ?? 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openDialog(room)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(room)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {rooms.length === 0 && (
            <div className="text-center py-12">
              <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Keine Räume vorhanden</p>
              <Button className="mt-4 bg-[#1a2f38]" onClick={() => openDialog()}>
                Ersten Raum erstellen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Raum bearbeiten' : 'Neuer Raum'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Raumname *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Terrasse"
                autoFocus
              />
            </div>
            <div>
              <Label>Sortierung</Label>
              <Input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1a2f38]">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
